import { type Dayjs } from 'dayjs';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import {
  buildTestCounts,
  type TestStatusKey,
} from '@/features/dashboard/components/dashboardStatusDataHelpers';
import { formatRangeWindowNoDataText } from '@/features/dashboard/hooks';
import { TestExecutionsTable } from '@/features/executions/components/TestExecutionsTable';
import { queryTestExecutionStatusSummary } from '@/features/executions/data';
import { EntityTrendsPageShell } from '@/features/shared/components/EntityTrendsPageShell';
import { useCloneDateWindow } from '@/features/shared/hooks/useCloneDateWindow';
import { useEntityTrendsPage } from '@/features/shared/hooks/useEntityTrendsPage';
import { resolveEntityTags } from '@/features/shared/tags';
import { TestsTable } from '@/features/tests/components/TestsTable';
import { TestsTableClone } from '@/features/tests/components/TestsTableClone';
import { queryTests, queryTestStatusSummary } from '@/features/tests/data/tests';
import { CloneToolbar, type CloneDateQuickFilter } from '@/features/tests/pages/TestsCloneToolbar';
import {
  cloneDefaultColumnVisibilityModel,
  cloneDefaultSortDirection,
  cloneDefaultSortKey,
  cloneSortFieldMap,
  type CloneSortDirection,
  type CloneSortKey,
} from '@/features/tests/pages/testsPageCloneConfig';
import { testExecutionSummaryStatusOptions } from '@/shared/constants/statusOptions';
import { gridPaths, queryKeys, useResolvedFeatureFlags } from '@/shared/hooks';
import { Stack } from '@/ui';
import { buildInFilter, mergeFilters } from '@/ui/composites/grid/gridFilters';
import { Panel } from '@/ui/composites/Panel';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridColumnVisibilityModel } from '@/ui/primitives';
import type {
  QueryResult,
  TestExecutionStatus,
  TestExecutionStatusSummaryDto,
  TestListItemDto,
  TestSummaryDto,
} from '@flowqa/shared';

export const TestsPage = (): ReactElement => {
  const { flags: featureFlags } = useResolvedFeatureFlags();
  const cloneTableEnabled = featureFlags?.testsCloneTable ?? false;
  const viewModel = useEntityTrendsPage<
    TestListItemDto,
    TestSummaryDto | TestExecutionStatusSummaryDto
  >({
    loadingMessage: 'Loading tests...',
    metricsScope: 'test',
    gridPath: gridPaths.tests,
    executionGridPath: gridPaths.testExecutions,
    entitySearchField: 'name',
    executionSearchField: 'testName',
    statusOptions: testExecutionSummaryStatusOptions.map((option) => ({
      value: option.value as Exclude<TestStatusKey, 'untested'>,
      label: option.label,
    })),
    summaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.testSummary(projectId, organisationId, filtersKey),
    executionSummaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.testExecutionSummary(projectId, organisationId, filtersKey),
    summaryPrefixKey: queryKeys.testSummaryPrefix,
    executionSummaryPrefixKey: queryKeys.testExecutionSummaryPrefix,
    queryLatestSummary: queryTestStatusSummary,
    queryExecutionSummary: queryTestExecutionStatusSummary,
    buildCounts: buildTestCounts,
    selectThresholds: (thresholds) => thresholds.tests,
    queryEntityTable: async (
      input: GridQueryInput,
      {
        selectedProjectId,
        selectedOrganisationId,
        rangeStartIso,
        rangeEndIso,
        previousRangeStartIso,
        previousRangeEndIso,
        deltasEnabled,
        tableDeltasEnabled,
      },
    ): Promise<QueryResult<TestListItemDto>> =>
      queryTests(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        metricsStartDay: rangeStartIso ?? undefined,
        metricsEndDay: rangeEndIso ?? undefined,
        previousMetricsStartDay: previousRangeStartIso ?? undefined,
        previousMetricsEndDay: previousRangeEndIso ?? undefined,
        include: {
          suites: { ids: true, items: true, primary: true },
          latestExecution: true,
          metrics: true,
          metricDeltas: deltasEnabled && tableDeltasEnabled,
        },
      }),
  });

  const [cloneDateQuickFilter, setCloneDateQuickFilter] = useState<CloneDateQuickFilter>('7d');
  const [cloneFromDate, setCloneFromDate] = useState<Dayjs | null>(null);
  const [cloneToDate, setCloneToDate] = useState<Dayjs | null>(null);
  const [debouncedCloneSearch, setDebouncedCloneSearch] = useState('');
  const [cloneColumnVisibilityModel, setCloneColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(cloneDefaultColumnVisibilityModel);
  const [cloneSortKey, setCloneSortKey] = useState<CloneSortKey>(cloneDefaultSortKey);
  const [cloneSortDirection, setCloneSortDirection] =
    useState<CloneSortDirection>(cloneDefaultSortDirection);
  const [cloneFilterStatuses, setCloneFilterStatuses] = useState<TestExecutionStatus[]>([]);
  const [cloneFilterTags, setCloneFilterTags] = useState<string[]>([]);
  const [cloneVisibleRows, setCloneVisibleRows] = useState<TestListItemDto[]>([]);

  const cloneAvailableTags = useMemo(() => {
    const tags = new Set<string>();
    cloneVisibleRows.forEach((row) => {
      resolveEntityTags(row).forEach((tag) => {
        tags.add(tag);
      });
    });
    return Array.from(tags.values()).sort((left, right) => left.localeCompare(right));
  }, [cloneVisibleRows]);
  const handleDebouncedCloneSearchChange = useCallback((value: string) => {
    setDebouncedCloneSearch(value);
  }, []);
  const showCloneDeltas = viewModel.deltasEnabled;
  const cloneDateWindow = useCloneDateWindow({
    dateQuickFilter: cloneDateQuickFilter,
    fromDate: cloneFromDate,
    toDate: cloneToDate,
  });
  const cloneQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<TestListItemDto>> => {
      const mappedFilters = input.filters.map((filter) =>
        filter.field === 'statusFilter'
          ? {
              ...filter,
              field: 'latestExecutionStatus',
            }
          : filter,
      );
      const toolbarFilters = mergeFilters(
        buildInFilter('latestExecutionStatus', cloneFilterStatuses),
        buildInFilter('tags', cloneFilterTags),
      );
      const sortOverride: ApiSort = {
        field: cloneSortFieldMap[cloneSortKey],
        direction: cloneSortDirection,
      };
      return queryTests(
        viewModel.selectedProjectId,
        viewModel.selectedOrganisationId ?? undefined,
        {
          ...input,
          sorts: [sortOverride],
          filters: [...mappedFilters, ...toolbarFilters],
          metricsStartDay: cloneDateWindow.startDay,
          metricsEndDay: cloneDateWindow.endDay,
          previousMetricsStartDay: cloneDateWindow.previousStartDay,
          previousMetricsEndDay: cloneDateWindow.previousEndDay,
          include: {
            suites: { ids: true, items: true, primary: true },
            latestExecution: true,
            metrics: true,
            metricDeltas: showCloneDeltas,
          },
        },
      );
    },
    [
      cloneDateWindow.endDay,
      cloneDateWindow.previousEndDay,
      cloneDateWindow.previousStartDay,
      cloneDateWindow.startDay,
      cloneFilterStatuses,
      cloneFilterTags,
      cloneSortDirection,
      cloneSortKey,
      showCloneDeltas,
      viewModel.selectedOrganisationId,
      viewModel.selectedProjectId,
    ],
  );
  const cloneNoRowsText = cloneDateWindow.hasCustomBounds
    ? 'No tests found for selected date range'
    : formatRangeWindowNoDataText('No tests found', cloneDateQuickFilter, false);
  const cloneStatusFilterKey = useMemo(
    () => [...cloneFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterStatuses],
  );
  const cloneTagFilterKey = useMemo(
    () => [...cloneFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterTags],
  );

  return (
    <Stack gap="md">
      <EntityTrendsPageShell
        viewModel={viewModel}
        title="Tests"
        addButtonLabel="Add Test"
        searchAriaLabel="Search tests"
        chartTabsAriaLabel="Tests chart tabs"
        pageTabsAriaLabel="Tests page tabs"
        entityTabLabel="Tests"
        chartEmptyStateTitle="No test executions found"
        entityTableNoRowsText="No tests found"
        executionTableNoRowsText="No test executions found"
        persistKey="tests-table"
        executionPersistKey="test-executions-table"
        renderEntityTable={({
          queryKey,
          queryFn,
          fixedFilters,
          showDeltas,
          persistKey,
          noRowsText,
        }) => (
          <TestsTable
            persistKey={persistKey}
            queryKey={queryKey}
            queryFn={queryFn}
            pageSizeOptions={[10]}
            initialPageSize={10}
            disablePageSizeSelect
            showDeltas={showDeltas}
            fixedFilters={fixedFilters}
            noRowsText={noRowsText}
          />
        )}
        renderExecutionsTable={({ fixedFilters, persistKey, noRowsText }) => (
          <TestExecutionsTable
            persistKey={persistKey}
            fixedFilters={fixedFilters}
            pageSizeOptions={[10]}
            initialPageSize={10}
            disablePageSizeSelect
            noRowsText={noRowsText}
          />
        )}
      />
      {cloneTableEnabled ? (
        <Panel padding="md" gap="none" contentGap="md" sx={{ backgroundColor: 'background.paper' }}>
          <CloneToolbar
            onDebouncedCloneSearchChange={handleDebouncedCloneSearchChange}
            columnVisibilityModel={cloneColumnVisibilityModel}
            onColumnVisibilityModelChange={setCloneColumnVisibilityModel}
            sortKey={cloneSortKey}
            sortDirection={cloneSortDirection}
            onSortChange={(nextSortKey, nextSortDirection) => {
              setCloneSortKey(nextSortKey);
              setCloneSortDirection(nextSortDirection);
            }}
            filterStatuses={cloneFilterStatuses}
            onFilterStatusesChange={setCloneFilterStatuses}
            filterTags={cloneFilterTags}
            onFilterTagsChange={setCloneFilterTags}
            dateQuickFilter={cloneDateQuickFilter}
            onDateQuickFilterChange={(next) => {
              setCloneDateQuickFilter(next);
              setCloneFromDate(null);
              setCloneToDate(null);
            }}
            fromDate={cloneFromDate}
            toDate={cloneToDate}
            onFromDateChange={setCloneFromDate}
            onToDateChange={setCloneToDate}
            availableTags={cloneAvailableTags}
          />
          <TestsTableClone
            persistKey="tests-table-clone"
            queryKey={[
              'tests-table-clone',
              cloneDateQuickFilter,
              viewModel.selectedOrganisationId ?? null,
              viewModel.selectedProjectId ?? null,
              showCloneDeltas ? 'deltas-on' : 'deltas-off',
              cloneSortKey,
              cloneSortDirection,
              cloneStatusFilterKey,
              cloneTagFilterKey,
              cloneDateWindow.startDay,
              cloneDateWindow.endDay,
            ]}
            queryFn={cloneQueryFn}
            pageSizeOptions={[10]}
            initialPageSize={10}
            disablePageSizeSelect
            showDeltas={showCloneDeltas}
            sparklineRangeDays={cloneDateWindow.sparklineRangeDays}
            metricsStartDay={cloneDateWindow.startDay}
            metricsEndDay={cloneDateWindow.endDay}
            noRowsText={cloneNoRowsText}
            showToolbar={false}
            disableColumnSorting
            disableColumnMenu
            search={debouncedCloneSearch}
            onRowsChange={(rows) => {
              setCloneVisibleRows(rows);
            }}
            columnVisibilityModel={cloneColumnVisibilityModel}
            onColumnVisibilityModelChange={(nextModel) => {
              setCloneColumnVisibilityModel({
                ...nextModel,
                name: true,
              });
            }}
          />
        </Panel>
      ) : null}
    </Stack>
  );
};
